import { Query } from '@apollo/client/react/components';
import React, { useState } from 'react';
import { compose, withProps } from 'recompose';
import get from 'lodash/get';
import { RouterlessModal } from '@/se/components/Modal';
import { breakpoint } from '@/se/utilities/responsive';
import { useMutation } from '@apollo/client';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import Form from '@/se/components/Form';
import { getSensorConfiguration, updateSensorGlobal } from '../../../graph/sensors';
import { CenteredSpinner } from '@/se/components/Spinner';
import NumberInput from '@/se/components/inputs/NumberInput';
import { withLabel } from '@/se/components/Label';
import SelectInput from '@/se/components/inputs/SelectInput';
import MultiSelectInput from '@/se/components/inputs/MultiSelectInput';
import { isDefinedNotNullAndFinite } from '@/se/utilities/check';
import { compact } from 'lodash/array';
import ReportRecipients from './ReportRecipients';
import { hoursOptions } from '@/util/timeOptions';
import IconButton from '@material-ui/core/IconButton';
import SettingsIcon from '@material-ui/icons/Settings';
import { Box } from '@material-ui/core';

export const hourOptions = [
  'Midnight',
  '1 AM',
  '2 AM',
  '3 AM',
  '4 AM',
  '5 AM',
  '6 AM',
  '7 AM',
  '8 AM',
  '9 AM',
  '10 AM',
  '11 AM',
  'Noon',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

const SensorGlobalConfigInput = withProps({
  schema: {
    gracePeriodMinutes: compose(
      withLabel('Grace period in minutes, allowing value to be out of bounds'),
      withProps({ required: true, min: 0 })
    )(NumberInput),
    temperatureUnit: compose(
      withLabel('Temperature unit'),
      withProps({
        required: true,
        options: {
          C: 'Celsius ℃',
          F: 'Fahrenheit ℉',
        },
      })
    )(SelectInput),
    measurementPoints: compose(
      withLabel('Daily moments relevant for the sensor report'),
      withProps({
        required: true,
        options: hourOptions.map((label, i) => ({ value: i, label })),
      })
    )(MultiSelectInput),
    reportRecipients: withLabel('Daily report recipients')(ReportRecipients),
    timeRange: compose(
      withLabel('Time Active', { section: true }),
      withProps({
        schema: {
          from: compose(
            withLabel('From'),
            withProps({
              options: hoursOptions,
              defaultValue: 0,
            })
          )(SelectInput),
          to: compose(
            withLabel('To'),
            withProps({
              options: {
                1: '1 AM',
                2: '2 AM',
                3: '3 AM',
                4: '4 AM',
                5: '5 AM',
                6: '6 AM',
                7: '7 AM',
                8: '8 AM',
                9: '9 AM',
                10: '10 AM',
                11: '11 AM',
                12: 'Noon',
                13: '1 PM',
                14: '2 PM',
                15: '3 PM',
                16: '4 PM',
                17: '5 PM',
                18: '6 PM',
                19: '7 PM',
                20: '8 PM',
                21: '9 PM',
                22: '10 PM',
                23: '11 PM',
                24: 'Midnight',
              },
              defaultValue: 24,
            })
          )(SelectInput),
        },
        render: ({ from, to }) => (
          <Box display="flex" justifyContent="stretch" style={{ gap: '1rem' }}>
            <Box flex={1}>{from}</Box>
            <Box flex={1}>{to}</Box>
          </Box>
        ),
        initialValue: { from: 0, to: 24 },
      })
    )(ObjectInput),
    autoResolve: compose(
      withLabel('Automatically resolve alerts at'),
      withProps({
        options: hourOptions.map((label, i) => ({ value: i, label })),
      })
    )(MultiSelectInput),
  },
})(ObjectInput);

const toSelectOptions = data => data.map(val => ({ value: val, label: hourOptions[val] }));
const mapInput = (val = {}) => ({
  ...val,
  measurementPoints: toSelectOptions(get(val, 'measurementPoints', [])),
  autoResolve: toSelectOptions(get(val, 'autoResolve', [])),
});

export default ({ style, refetch }) => {
  const [open, setOpen] = useState(false);

  const [mutate] = useMutation(updateSensorGlobal);

  const handleSubmit = async values => {
    const result = await mutate({
      variables: {
        gracePeriodMinutes: get(values, 'gracePeriodMinutes'),
        temperatureUnit: get(values, 'temperatureUnit'),
        measurementPoints: get(values, 'measurementPoints', [])
          .map(_ => _.value)
          .filter(isDefinedNotNullAndFinite),
        reportRecipients: compact(get(values, 'reportRecipients', [])),
        timeRange: {
          from: parseInt(get(values, 'timeRange.from', 0), 10),
          to: parseInt(get(values, 'timeRange.to', 24), 10),
        },
        autoResolve: get(values, 'autoResolve', [])
          .map(_ => _.value)
          .filter(isDefinedNotNullAndFinite),
      },
      refetchQueries: compact([{ query: getSensorConfiguration }, refetch]),
    });

    if (get(result, 'data.updateSensorGlobal')) {
      setOpen(false);
    }
  };

  return (
    <Query query={getSensorConfiguration}>
      {({ loading, data = {} }) => (
        <div>
          <IconButton onClick={() => setOpen(true)} style={style}>
            <SettingsIcon />
          </IconButton>
          {open && (
            <RouterlessModal
              title={'Sensor Global Configuration'}
              onClose={() => setOpen(false)}
              fullWindow={window.innerWidth < breakpoint.md}
            >
              {!loading ? (
                <Form
                  autoFocus
                  initialValue={mapInput(get(data, 'sensorConfiguration'))}
                  input={SensorGlobalConfigInput}
                  label={'Save'}
                  onSubmit={handleSubmit}
                  onCancel={() => setOpen(false)}
                />
              ) : (
                <CenteredSpinner />
              )}
            </RouterlessModal>
          )}
        </div>
      )}
    </Query>
  );
};
